import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { useSelector } from 'react-redux'; // Import hook
import EmojiPicker from 'emoji-picker-react';
import SearchBar from '../../components/Common/SearchBar/SearchBar'; // Added SearchBar import
import './ChatSystem.css';

// FontAwesome Icons
import {
  faPlus,
  faEllipsisV,
  faSearch,
  faInfoCircle,
  faPhone,
  faVideo,
  faImage,
  faSmile,
  faPaperPlane,
  faPlane,
  faPen,
  faTrash,
  faTimes,
  faCheck,
  faCheckDouble,
  faPalette,
  faExclamationTriangle,
  faFlag,
  faPaperclip,
  faArrowTrendUp
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { getApiOrigin } from '../../utils/assetUrls';

const SOCKET_URL =
  typeof window !== 'undefined' ? getApiOrigin() : 'http://localhost:3000';

const ChatSystem = () => {
  // Redux State
  const { user: reduxUser } = useSelector((state) => state.auth);

  // Local State
  const [currentUser, setCurrentUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [socket, setSocket] = useState(null);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // Profile Sidebar State
  const [showProfileInfo, setShowProfileInfo] = useState(false);
  const [profileUser, setProfileUser] = useState(null);

  // Theme State
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [themeColor, setThemeColor] = useState('#5c6bc0'); // Default Purple

  // Message Filtering
  const [showMsgSearch, setShowMsgSearch] = useState(false);
  const [msgSearchTerm, setMsgSearchTerm] = useState("");

  // Emoji & Upload
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const fileInputRef = useRef(null);

  // Typing State
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  // Patient Context Search (Live)
  const [patientSearchTerm, setPatientSearchTerm] = useState("");
  const [selectedPatientContext, setSelectedPatientContext] = useState(null);
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [isSearchingPatients, setIsSearchingPatients] = useState(false);
  const [isSnapshotDropdownOpen, setIsSnapshotDropdownOpen] = useState(false);
  const patientSearchTimeoutRef = useRef(null);

  const fetchPatientSearchResults = async (searchTerm = '') => {
    if (!currentUser?.practice_id) {
       setPatientSearchResults([]);
       return;
    }
    setIsSearchingPatients(true);
    try {
       const res = await axios.get(`/api/practices/${currentUser.practice_id}/patients?search=${encodeURIComponent(searchTerm)}`);
       if(res.data.success) {
           setPatientSearchResults(res.data.data.patients || []);
       }
    } catch(err) {
       console.error("Failed to search patients", err);
    } finally {
       setIsSearchingPatients(false);
    }
  };

  const handlePatientSearchChange = (val) => {
     setPatientSearchTerm(val);
     if (patientSearchTimeoutRef.current) clearTimeout(patientSearchTimeoutRef.current);
     setIsSnapshotDropdownOpen(true);
     patientSearchTimeoutRef.current = setTimeout(() => {
        fetchPatientSearchResults(val);
     }, 300);
  };

  const handlePatientSearchFocus = () => {
     setIsSnapshotDropdownOpen(true);
     if (patientSearchResults.length === 0) {
        fetchPatientSearchResults(patientSearchTerm);
     }
  };

  const handlePatientSearchBlur = () => {
     setTimeout(() => setIsSnapshotDropdownOpen(false), 200);
  };

  // Refs
  const messagesEndRef = useRef(null);
  const currentChatRef = useRef(null);
  useEffect(() => {
    currentChatRef.current = currentChat;
  }, [currentChat]);

  const fetchConversations = useCallback(async (userId) => {
    try {
      const { data } = await axios.get(`/api/chat/conversations/${userId}`);
      if (data.success) {
        setConversations(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    }
  }, []);

  // 1. Initialize User & Socket
  useEffect(() => {
    // Prefer Redux user, fallback to local storage if Redux is empty (e.g. on hard refresh before persist rehydration)
    let activeUser = reduxUser;

    if (!activeUser) {
      const storedUserStr = localStorage.getItem('user') || localStorage.getItem('userInfo');
      if (storedUserStr) {
        try {
          activeUser = JSON.parse(storedUserStr);
        } catch (e) {
          console.error("Error parsing stored user", e);
        }
      }
    }

    if (activeUser) {
      setCurrentUser(activeUser);
      console.log("ChatSystem initialized with user:", activeUser);

      const newSocket = io(SOCKET_URL, {
        transports: ['websocket', 'polling'],
        withCredentials: true,
        reconnectionDelay: 500,
        reconnectionDelayMax: 4000
      });
      setSocket(newSocket);

      newSocket.on('connect', () => {
        newSocket.emit('join-chat', activeUser.id);
      });

      newSocket.on('messages_read', (data) => {
        const chat = currentChatRef.current;
        if (chat && data.by_user_id === chat.other_user_id) {
          setMessages((prev) => prev.map((m) =>
            (m.from_user_id === activeUser.id ? { ...m, is_read: 1 } : m)
          ));
        }
      });

      newSocket.on('typing', (data) => {
        const chat = currentChatRef.current;
        if (chat && data.from_user_id === chat.other_user_id) {
          setIsTyping(true);
        }
      });

      newSocket.on('stop_typing', (data) => {
        const chat = currentChatRef.current;
        if (chat && data.from_user_id === chat.other_user_id) {
          setIsTyping(false);
        }
      });

      newSocket.on('receive_message', (msg) => {
        const chat = currentChatRef.current;
        if (
          chat &&
          (msg.from_user_id === chat.other_user_id || msg.to_user_id === chat.other_user_id)
        ) {
          setMessages((prev) => {
            if (msg.id != null && prev.some((m) => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
        }
      });

      return () => {
        newSocket.disconnect();
      };
    } else {
      console.warn("ChatSystem: No user found in Redux or localStorage");
    }
  }, [reduxUser]);

  // 2. Fetch Conversations when user is set (not on every message — that caused lag)
  useEffect(() => {
    if (currentUser) {
      fetchConversations(currentUser.id);
    }
  }, [currentUser, fetchConversations]);

  // 3. Fetch Messages when chat is selected
  useEffect(() => {
    if (currentChat && currentUser) {
      fetchHistory(currentUser.id, currentChat.other_user_id);
    }
  }, [currentChat, currentUser]);

  const fetchHistory = async (myId, otherId) => {
    try {
      const { data } = await axios.get(`/api/chat/history/${myId}/${otherId}`);
      if (data.success) {
        setMessages(data.data);
        scrollToBottom();
      }
    } catch (err) {
      console.error("Failed to fetch history:", err);
    }
  };

  const scrollToBottom = (smooth = false) => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    });
  };



  const handleInputCheck = (e) => {
    const val = e.target.value;
    setInputText(val);

    if (!socket || !currentChat || !currentUser) return;

    // Emit typing
    socket.emit('typing', {
      from_user_id: currentUser.id,
      to_user_id: currentChat.other_user_id
    });

    // Clear existing timeout
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    // Set timeout to stop typing
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('stop_typing', {
        from_user_id: currentUser.id,
        to_user_id: currentChat.other_user_id
      });
    }, 600);
  };

  // 4. Handle Send
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || !currentUser || !currentChat) return;

    const text = inputText.trim();
    const tempId = `pending-${Date.now()}`;
    const optimistic = {
      id: tempId,
      from_user_id: currentUser.id,
      to_user_id: currentChat.other_user_id,
      message: text,
      created_at: new Date().toISOString(),
      is_read: 0,
      _pending: true
    };

    setMessages((prev) => [...prev, optimistic]);
    setInputText('');
    scrollToBottom(false);

    const payload = {
      from_user_id: currentUser.id,
      to_user_id: currentChat.other_user_id,
      message: text,
      practice_id: currentUser.practice_id
    };

    try {
      const { data } = await axios.post('/api/chat/send', payload);

      if (data.success) {
        setMessages((prev) => prev.map((m) => (m.id === tempId ? data.data : m)));
        fetchConversations(currentUser.id);
        scrollToBottom(false);
      }
    } catch (err) {
      console.error('Send failed:', err);
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setInputText(text);
    }
  };

  // 5. Handle Start Chat (Modal)
  const handleEmojiClick = (emojiData) => {
    setInputText(prev => prev + emojiData.emoji);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      // 1. Upload File
      const uploadRes = await axios.post('/api/chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (uploadRes.data.success) {
        const { file_path, file_type, original_file_name } = uploadRes.data.data;

        // 2. Send Message with Attachment
        const payload = {
          from_user_id: currentUser.id,
          to_user_id: currentChat.other_user_id,
          message: "", // Empty text for image-only message, or generic text
          file_path,
          file_type,
          original_file_name,
          practice_id: currentUser.practice_id
        };

        const { data } = await axios.post('/api/chat/send', payload);
        if (data.success) {
          setMessages((prev) => [...prev, data.data]);
          scrollToBottom();
        }
      }
    } catch (err) {
      console.error("Upload failed", err);
      alert("File upload failed");
    }
  };

  const openNewChatModal = async () => {
    // Use state currentUser, fallback to Redux user
    let activeUser = currentUser || reduxUser;

    // Last resort fallback
    if (!activeUser) {
      const stored = localStorage.getItem('user');
      if (stored) activeUser = JSON.parse(stored);
    }

    console.log("Opening chat modal with user:", activeUser);

    if (!activeUser) {
      alert("User session not found. Please reload or log in.");
      return;
    }

    setIsModalOpen(true);
    setAvailableUsers([]);
    setIsLoadingUsers(true);

    try {
      const pId = activeUser.practice_id || 'null';
      const url = `/api/chat/users/${pId}/${activeUser.id}`;

      const { data } = await axios.get(url);

      if (data.success) {
        setAvailableUsers(data.data);
      } else {
        console.error("Fetch success false:", data);
      }
    } catch (err) {
      console.error("Users fetch error:", err);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const startNewConversation = (user) => {
    // metadata for the new/existing chat
    const chatObj = {
      other_user_id: user.id,
      other_user_name: user.name || user.username || user.first_name,
      other_user_avatar: user.avatar,
      // created_at, message etc will be empty initially for new
    };

    // Check if we already have this conversation in our list
    const existing = conversations.find(c => c.other_user_id === user.id);
    if (existing) {
      setCurrentChat(existing);
    } else {
      setCurrentChat(chatObj);
    }

    // Reset states
    setIsTyping(false);
    setShowProfileInfo(false); // Close profile if switching chat

    setIsModalOpen(false);
  };

  const handleProfileClick = async () => {
    if (!currentChat) return;
    if (showProfileInfo) {
      setShowProfileInfo(false);
      return;
    }

    setShowProfileInfo(true);
    // Fetch fresh details
    try {
      const { data } = await axios.get(`/api/chat/user/${currentChat.other_user_id}`);
      if (data.success) {
        setProfileUser(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch profile:", err);
    }
  };

  // Theme Handler
  const handleThemeChange = (color) => {
    setThemeColor(color);
    document.documentElement.style.setProperty('--primary-color', color);
    setShowThemeMenu(false);
  };

  const themeOptions = [
    '#5c6bc0', // Purple (Default)
    '#1a73e8', // Blue
    '#e53935', // Red
    '#43a047', // Green
    '#fb8c00', // Orange
    '#8e24aa', // Violet
    '#3949ab', // Indigo
    '#00acc1', // Cyan
  ];

  // Helpers
  const getInitials = (name) => name ? name.charAt(0).toUpperCase() : "?";
  const getColorIndex = (name) => name ? (name.charCodeAt(0) % 6) + 1 : 1;
  const formatTime = (ts) => {
    if (!ts) return "";
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderTicks = (msg) => {
    if (msg.from_user_id !== currentUser?.id) return null; // Only show ticks for my messages

    // Logic: 
    // is_read = 1 -> Double Blue
    // is_read = 0 -> Single or Double Grey (Simulated "Delivered")

    if (msg.is_read) {
      return <FontAwesomeIcon icon={faCheckDouble} className="tick-icon tick-read-on-primary" title="Read" />;
    }
    return <FontAwesomeIcon icon={faCheck} className="tick-icon tick-sent" title="Sent" />;
  };

  // Filtered lists
  const filteredConversations = conversations.filter(c =>
    c.other_user_name?.toLowerCase().includes(inputText.toLowerCase())
  );

  // Actually, inputText is for message input. We need separate search states.
  const [searchTerm, setSearchTerm] = useState("");
  const [modalSearchTerm, setModalSearchTerm] = useState("");

  const filteredConvos = conversations.filter(c =>
    c.other_user_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAvailableUsers = availableUsers.filter(u =>
    (u.name || u.username || '').toLowerCase().includes(modalSearchTerm.toLowerCase())
  );

  // -- RENDER --
  return (
    <div className="chat-wrapper">

      {/* 1. LEFT SIDEBAR: Messages / Staff List */}
      <div className="chat-sidebar">
        <div className="sidebar-header">
          <span className="sidebar-title">Messages</span>
          <button className="btn-icon" style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
            <FontAwesomeIcon icon={faEllipsisV} color="#5f6368" />
          </button>
        </div>

        <div className="sidebar-search">
          <SearchBar 
            value={searchTerm}
            onChange={(val) => setSearchTerm(val)}
            placeholder="Search conversations"
            label=""
            className="chat-sidebar-search"
          />
          
          <div className="sidebar-filters">
            <span className="filter-item active">All</span>
            <span className="filter-divider">|</span>
            <span className="filter-item">Unread</span>
            <span className="filter-divider">|</span>
            <span className="filter-item">Flagged</span>
          </div>
        </div>

        <div className="conversation-list">
          {filteredConvos.map((c) => {
            const isAlert = c.other_user_name === "John Doe"; // Mock logic for design
            const isFlagged = c.other_user_name === "John Doe"; // Mock logic
            return (
              <div
                key={c.id || c.other_user_id}
                className={`conversation-item ${currentChat?.other_user_id === c.other_user_id ? 'active' : ''}`}
                onClick={() => setCurrentChat(c)}
              >
                <div className={`avatar avatar-${getColorIndex(c.other_user_name)}`}>
                  {c.other_user_avatar ? <img src={c.other_user_avatar} alt="av" style={{ width: '100%', borderRadius: '50%' }} /> : getInitials(c.other_user_name)}
                  {c.other_user_name === "John Doe" && <div className="status-indicator online"></div>}
                </div>
                <div className="conv-info">
                  <div className="conv-top">
                    <span className="conv-name">
                      {c.other_user_name}
                    </span>
                    <div className="conv-badges">
                      {isAlert && <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning" />}
                      {isFlagged && <FontAwesomeIcon icon={faFlag} className="text-danger" style={{ marginLeft: '4px' }} />}
                    </div>
                  </div>
                  <div className="conv-id-sub">ID: {c.other_user_id || '8821'}</div>
                  <div className="conv-preview">
                    {c.message || (isAlert ? "Feeling a little dizzy after..." : "Latest BP readings are in.")}
                  </div>
                </div>
              </div>
            );
          })}
          {filteredConvos.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>
              No conversations found
            </div>
          )}
        </div>

        <button className="btn-start-chat-text" onClick={openNewChatModal}>
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>

      {/* 2. CHAT MAIN: Chat Conversation */}
      <div className={`chat-main`}>
        {currentChat ? (
          <>
            <div className="chat-header">
              <div className="chat-header-user" onClick={handleProfileClick} style={{ cursor: 'pointer' }}>
                <div className={`avatar avatar-${getColorIndex(currentChat.other_user_name)}`} style={{ width: 40, height: 40, fontSize: 16 }}>
                  {getInitials(currentChat.other_user_name)}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '12px' }}>
                  <span className="chat-header-name" style={{ marginLeft: 0 }}>{currentChat.other_user_name}</span>
                  <span className="chat-header-id" style={{ fontSize: '12px', color: '#6c757d' }}>ID: {currentChat.other_user_id || '8821'}</span>
                </div>
              </div>
              
              <div className="chat-header-alert">
                 {currentChat.other_user_name === "John Doe" && (
                    <div className="alert-badge">
                      <span className="alert-dot"></span> High Risk (BP Alert)
                    </div>
                 )}
              </div>

              <div className="chat-actions">
                  <button className="btn-icon" style={{ marginLeft: '12px' }}><FontAwesomeIcon icon={faEllipsisV} color="#5f6368" /></button>
              </div>
            </div>

            <div className="message-list">
              {currentChat.other_user_name === "John Doe" && (
                <div className="system-alert-bubble">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger" style={{ marginRight: '8px' }} />
                  <strong>ALERT:</strong> BP reading 160/100 mmHg detected.
                  <span className="msg-time-inline">9:01 AM</span>
                </div>
              )}
              
              {messages
                .filter(m => {
                  const isChatMatch = (m.from_user_id === currentUser?.id && m.to_user_id === currentChat.other_user_id) ||
                    (m.from_user_id === currentChat.other_user_id && m.to_user_id === currentUser?.id);
                  if (!isChatMatch) return false;
                  if (!msgSearchTerm) return true;
                  return (m.message || "").toLowerCase().includes(msgSearchTerm.toLowerCase());
                })
                .map((msg, i) => {
                  const isMe = msg.from_user_id === currentUser?.id;
                  return (
                    <div key={i} className={`message-row ${isMe ? 'sent' : 'received'}`}>
                      <div className={`message-bubble`} style={{ padding: msg.file_path ? '4px' : '10px 16px' }}>
                        {msg.file_path && (
                          <div className="msg-attachment" style={{ marginBottom: msg.message ? 8 : 0 }}>
                            {msg.file_type?.startsWith('image') ? (
                              <img
                                src={`${getApiOrigin()}${msg.file_path}`}
                                alt="attachment"
                                style={{ maxWidth: '200px', borderRadius: '8px', cursor: 'pointer' }}
                                onClick={() => window.open(`${getApiOrigin()}${msg.file_path}`, '_blank')}
                              />
                            ) : (
                              <a href={`${getApiOrigin()}${msg.file_path}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                📎 {msg.original_file_name || 'Attachment'}
                              </a>
                            )}
                          </div>
                        )}
                        {msg.message && <div>{msg.message}</div>}
                      </div>
                      <div className="message-meta-outside">
                        <span className="msg-time">{formatTime(msg.created_at)}</span>
                        {isMe && renderTicks(msg)}
                      </div>
                    </div>
                  );
                })}
              <div ref={messagesEndRef} />
            </div>

            <div style={{ position: 'relative' }}>
              {showEmojiPicker && (
                <div style={{ position: 'absolute', bottom: '80px', left: '20px', zIndex: 100 }}>
                  <div className="emoji-picker-container">
                    <EmojiPicker onEmojiClick={handleEmojiClick} />
                  </div>
                </div>
              )}
            </div>

            <div className="chat-input-area-wrapper">
               <form className="chat-input-container" onSubmit={handleSendMessage}>
                 <input
                   type="file"
                   ref={fileInputRef}
                   style={{ display: 'none' }}
                   onChange={handleFileUpload}
                   accept="image/*,.pdf,.doc,.docx"
                 />
                 <button type="button" className="btn-attachment" title="Attach file" onClick={() => fileInputRef.current.click()}>
                    <FontAwesomeIcon icon={faPaperclip} />
                 </button>
                 <div className="input-field-wrapper">
                   <input
                     className="input-field"
                     placeholder="Type a message..."
                     value={inputText}
                     onChange={handleInputCheck}
                   />
                 </div>
                 <button type="submit" className="btn-send-primary" disabled={!inputText.trim()}>
                   Send
                 </button>
               </form>
            </div>
          </>
        ) : (
          <div className="chat-empty-state">
            <div className="empty-state-icon-wrapper">
              <FontAwesomeIcon icon={faPaperPlane} />
            </div>
            <h2 className="empty-state-title">Your Messages</h2>
            <p className="empty-state-desc">Select a contact from the list<br/>or start a new conversation to collaborate.</p>
          </div>
        )}
      </div>

      {/* 3. RIGHT SIDEBAR: Focused Vital Snapshot */}
      <div className="patient-snapshot">
         <div className="snapshot-header">
            <h3>Focused Vital Snapshot</h3>
         </div>
         
         <div className="patient-search-wrapper" style={{ position: 'relative' }}>
            <SearchBar 
              value={patientSearchTerm}
              onChange={handlePatientSearchChange}
              onFocus={handlePatientSearchFocus}
              onBlur={handlePatientSearchBlur}
              placeholder="Search patients by name or ID..."
              label=""
              className="chat-snapshot-search"
            />

            {/* Live Search Dropdown */}
            {isSnapshotDropdownOpen && (patientSearchResults.length > 0 || isSearchingPatients) && (
               <div className="patient-search-dropdown" style={{
                   position: 'absolute', top: '100%', left: 0, right: 0, 
                   background: '#ffffff', border: '1px solid var(--border-color)', 
                   borderRadius: '0 0 8px 8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                   maxHeight: '250px', overflowY: 'auto', zIndex: 100
               }}>
                  {isSearchingPatients ? (
                     <div style={{padding: '12px 14px', color: 'var(--text-secondary)', fontSize: '13px', textAlign: 'center'}}>
                        Searching...
                     </div>
                  ) : (
                     patientSearchResults.map(p => (
                        <div key={p.id} 
                             style={{padding: '10px 14px', borderBottom: '1px solid #f7fafc', cursor: 'pointer', display: 'flex', flexDirection: 'column', transition: 'background 0.2s'}}
                             onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                             onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                             onClick={() => {
                                setSelectedPatientContext({
                                   name: `${p.first_name} ${p.last_name}`,
                                   id: p.patient_id || p.id
                                });
                                setPatientSearchTerm('');
                                setPatientSearchResults([]);
                             }}>
                             <span style={{fontWeight: 600, fontSize: '13.5px', color: 'var(--text-primary)'}}>{p.first_name} {p.last_name}</span>
                             <span style={{fontSize: '11px', color: 'var(--sidebar-text-muted)'}}>ID: {p.patient_id || p.id}</span>
                        </div>
                     ))
                  )}
               </div>
            )}
         </div>

         {selectedPatientContext ? (() => {
            const contextData = selectedPatientContext;
            return (
              <div className="snapshot-content">
                 {/* Concise Patient Vital Card */}
                 <div className="concise-vital-card">
                    <div className="cvc-user">
                       <div className={`avatar avatar-${getColorIndex(contextData.name)}`} style={{width: 36, height: 36, fontSize: 14}}>
                         {getInitials(contextData.name)}
                       </div>
                       <div className="cvc-user-info">
                          <span className="cvc-name">{contextData.name}</span>
                          <span className="cvc-id">ID: {contextData.id}</span>
                       </div>
                    </div>
                    
                    <div className="cvc-divider"></div>

                    <div className="cvc-vitals">
                       <div className="cvc-vital-col">
                          <span className="cvc-label">BP</span>
                          <span className="cvc-value">160</span>
                       </div>
                       <div className="cvc-vital-col">
                          <span className="cvc-label">HR</span>
                          <span className="cvc-value">170</span>
                       </div>
                       <div className="cvc-vital-col">
                          <span className="cvc-label">SpO2 <FontAwesomeIcon icon={faArrowTrendUp} className="text-success" style={{fontSize: '10px'}}/></span>
                          <span className="cvc-value">94%</span>
                       </div>
                    </div>
                 </div>
              </div>
            );
         })() : (
            <div className="snapshot-empty">
               <div className="snapshot-empty-container">
                  <p>Search and select a patient to view their vital snapshot context.</p>
               </div>
            </div>
         )}
      </div>

      {/* MODAL */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>New Conversation</h3>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-search">
              <input
                placeholder="Type a name to search..."
                autoFocus
                value={modalSearchTerm}
                onChange={(e) => setModalSearchTerm(e.target.value)}
              />
            </div>

            <div className="modal-body">
              {isLoadingUsers && <p style={{ padding: 16, textAlign: 'center' }}>Loading users...</p>}
              {!isLoadingUsers && filteredAvailableUsers.length === 0 && (
                <p style={{ padding: 16, textAlign: 'center' }}>No users found.</p>
              )}
              {filteredAvailableUsers.map(u => (
                <button key={u.id} className="modal-user-item" onClick={() => startNewConversation(u)}>
                  <div className={`avatar avatar-${getColorIndex(u.name || u.username)}`}>
                    {getInitials(u.name || u.username)}
                  </div>
                  <div className="modal-user-info">
                    <div className="modal-user-name">{u.name || u.username}</div>
                    <div className="modal-user-role">{u.role_name}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ChatSystem;
